import {
  durationOptions,
  popularityOptions,
  timeOptions,
  countActiveFilters,
  type FilterState,
} from "@/utils/filter";

interface FilterPanelProps {
  subs: string[];
  filters: FilterState;
  onChange: (next: FilterState) => void;
  onReset: () => void;
}

export default function FilterPanel({ subs, filters, onChange, onReset }: FilterPanelProps) {
  const toggleInArray = (key: "subs" | "popularity" | "durations", value: string) => {
    const current = filters[key];
    const next = current.includes(value)
      ? current.filter((x) => x !== value)
      : [...current, value];
    onChange({ ...filters, [key]: next });
  };

  const active = countActiveFilters(filters);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between">
        <h3 className="font-heading text-sm font-bold text-foreground-100">
          筛选条件 {active > 0 && <span className="text-primary-400">({active})</span>}
        </h3>
        <button
          type="button"
          onClick={onReset}
          className="flex items-center gap-1 text-xs font-semibold text-foreground-500 transition-colors hover:text-primary-400"
        >
          <i className="ri-refresh-line" /> 重置
        </button>
      </div>

      {subs.length > 0 && (
        <div>
          <h4 className="mb-3 font-label text-xs font-semibold uppercase tracking-wide text-foreground-500">
            子分类
          </h4>
          <div className="flex flex-wrap gap-2">
            {subs.map((sub) => {
              const on = filters.subs.includes(sub);
              return (
                <button
                  key={sub}
                  type="button"
                  onClick={() => toggleInArray("subs", sub)}
                  className={`whitespace-nowrap rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                    on
                      ? "border-primary-500 bg-primary-500/15 text-primary-300"
                      : "border-background-700 text-foreground-400 hover:border-background-600 hover:text-foreground-200"
                  }`}
                >
                  {sub}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div>
        <h4 className="mb-3 font-label text-xs font-semibold uppercase tracking-wide text-foreground-500">
          主播人气
        </h4>
        <div className="flex flex-col gap-2">
          {popularityOptions.map((opt) => {
            const on = filters.popularity.includes(opt.value);
            return (
              <label
                key={opt.value}
                className="flex cursor-pointer items-center gap-2.5 text-sm text-foreground-300"
              >
                <span
                  className={`flex h-4 w-4 items-center justify-center rounded border transition-colors ${
                    on ? "border-primary-500 bg-primary-500 text-background-950" : "border-background-600"
                  }`}
                >
                  {on && <i className="ri-check-line text-[11px]" />}
                </span>
                <input
                  type="checkbox"
                  className="hidden"
                  checked={on}
                  onChange={() => toggleInArray("popularity", opt.value)}
                />
                {opt.label}
              </label>
            );
          })}
        </div>
      </div>

      <div>
        <h4 className="mb-3 font-label text-xs font-semibold uppercase tracking-wide text-foreground-500">
          视频时长
        </h4>
        <div className="flex flex-col gap-2">
          {durationOptions.map((opt) => {
            const on = filters.durations.includes(opt.value);
            return (
              <label
                key={opt.value}
                className="flex cursor-pointer items-center gap-2.5 text-sm text-foreground-300"
              >
                <span
                  className={`flex h-4 w-4 items-center justify-center rounded border transition-colors ${
                    on ? "border-primary-500 bg-primary-500 text-background-950" : "border-background-600"
                  }`}
                >
                  {on && <i className="ri-check-line text-[11px]" />}
                </span>
                <input
                  type="checkbox"
                  className="hidden"
                  checked={on}
                  onChange={() => toggleInArray("durations", opt.value)}
                />
                {opt.label}
              </label>
            );
          })}
        </div>
      </div>

      <div>
        <h4 className="mb-3 font-label text-xs font-semibold uppercase tracking-wide text-foreground-500">
          发布时间
        </h4>
        <div className="flex flex-col gap-2">
          {timeOptions.map((opt) => {
            const on = filters.time === opt.value;
            return (
              <label
                key={opt.value}
                className="flex cursor-pointer items-center gap-2.5 text-sm text-foreground-300"
              >
                <span
                  className={`flex h-4 w-4 items-center justify-center rounded-full border transition-colors ${
                    on ? "border-primary-500" : "border-background-600"
                  }`}
                >
                  {on && <span className="h-2 w-2 rounded-full bg-primary-500" />}
                </span>
                <input
                  type="radio"
                  name="time"
                  className="hidden"
                  checked={on}
                  onChange={() => onChange({ ...filters, time: opt.value })}
                />
                {opt.label}
              </label>
            );
          })}
        </div>
      </div>
    </div>
  );
}